import { useState, type CSSProperties } from 'react';

/**
 * How something new arrives (spec 097, reused by spec 122).
 *
 * The item opens the room; the content fades into it once the room is there.
 * `grid` on the item is what the growth animates (a row from 0fr to 1fr), and
 * `min-h-0` on the content is what lets that row start at nothing. Both are
 * `motion-safe:` so reduced motion gets a plain insert with no branch in code.
 *
 * `min-w-0` is the same release across: the content is a grid item, whose minimum
 * width is otherwise its min-content width, and the item stays a grid after the
 * animation. A new board card whose description carried long URLs grew past its
 * column and covered the next one until the page was reloaded.
 * The keyframes live in `globals.css` (`arrive-grow`, `arrive-fade`).
 *
 * These were private to the transcript list; the board and the Tasks table
 * now arrive the same way (spec 122 FR-023), so they live here.
 */
export const ARRIVAL_ITEM_CLASS = 'grid motion-safe:animate-arrive-grow';
export const ARRIVAL_CONTENT_CLASS =
  'min-h-0 min-w-0 motion-safe:animate-arrive-fade';

/**
 * The fade step on its own, for a row that is positioned absolutely — a
 * virtualised table places rows with `translateY`, so a grid row has nothing
 * to grow into. The fade still says "this is new" without fighting the
 * transform. No `backwards` delay: without the growth there is nothing to wait
 * for.
 */
export const ARRIVAL_FADE_CLASS = 'motion-safe:animate-in motion-safe:fade-in';

/**
 * A row that slides into a short list as it fades (spec 196 US6): someone just
 * added to a workspace's members, a request just granted. A few pixels down, at
 * the scale's `base`, which is long enough to be seen and short of a wait. All
 * `motion-safe:`, so reduced motion gets the row in place with nothing moving.
 */
export const ARRIVAL_SLIDE_CLASS =
  'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:animation-duration-[var(--motion-duration-base)]';

/** The first-paint entrance each staggered row plays (spec 122 FR-024). */
export const STAGGER_CLASS = 'motion-safe:animate-in motion-safe:fade-in';

/** How far apart the first rows land on a fresh page, in milliseconds. */
export const STAGGER_STEP_MS = 25;

/**
 * The delay for one row in a first-paint stagger (spec 122 FR-024).
 *
 * Only the first `limit` rows are staggered; past that the eye has stopped
 * following and every further delay is just lateness. Returns `undefined`
 * rather than a zero delay so the caller can spread it into `style` without
 * writing a property it does not need.
 */
export function staggerStyle(
  index: number,
  limit = 8,
): CSSProperties | undefined {
  if (index < 0 || index >= limit) return undefined;
  return { animationDelay: `${String(index * STAGGER_STEP_MS)}ms` };
}

interface Seen {
  ids: ReadonlySet<string>;
  fresh: ReadonlySet<string>;
}

function sameIds(ids: readonly string[], seen: ReadonlySet<string>): boolean {
  if (ids.length !== seen.size) return false;
  for (const id of ids) if (!seen.has(id)) return false;
  return true;
}

/**
 * Which of these items were not here on the previous render.
 *
 * The first list seeds the record and reports nothing new: a page that has
 * just opened is not a page something has arrived on, which is what keeps the
 * arrival cue (this) and the first-paint stagger (`useInitialIds`) apart. The
 * seed is the first *non-empty* list, because a page mounts before its data
 * and an empty first render would make every row an arrival the moment the
 * data came. From then on, an id absent from the last list is fresh until the
 * list changes again — long enough for the arrival animation to play once,
 * and the class leaving afterwards changes nothing visible.
 *
 * State adjusted during render rather than a ref read during render: the
 * record only moves when the ids differ, which is the documented shape for
 * deriving from a prop, and it keeps the hook pure for the compiler's rules.
 */
export function useNewIds<T extends { id: string }>(
  items: readonly T[],
): ReadonlySet<string> {
  const [seen, setSeen] = useState<Seen>(() => ({
    ids: new Set(items.map((item) => item.id)),
    fresh: new Set(),
  }));
  // Distinct ids: a list can name one item twice (a board whose Backlog is
  // both the synthesized start step and a phase step lists its tasks in both),
  // and a list that is never the same size as its own set would set state on
  // every render — React's "too many re-renders".
  const ids = [...new Set(items.map((item) => item.id))];
  if (!sameIds(ids, seen.ids)) {
    const fresh =
      seen.ids.size === 0
        ? new Set<string>()
        : new Set(ids.filter((id) => !seen.ids.has(id)));
    setSeen({ ids: new Set(ids), fresh });
    return fresh;
  }
  return seen.fresh;
}

/**
 * The ids a list held when it first had any — the rows that were there at
 * first paint and so may land a beat apart (`staggerStyle`), as opposed to the
 * rows that arrived later (`useNewIds`). Never updated after that first list.
 */
export function useInitialIds<T extends { id: string }>(
  items: readonly T[],
): ReadonlySet<string> {
  const [initial, setInitial] = useState<ReadonlySet<string> | null>(() =>
    items.length > 0 ? new Set(items.map((item) => item.id)) : null,
  );
  if (initial === null && items.length > 0) {
    const ids = new Set(items.map((item) => item.id));
    setInitial(ids);
    return ids;
  }
  return initial ?? EMPTY;
}

const EMPTY: ReadonlySet<string> = new Set();
